// web/src/admin/AdminBoard.tsx — /admin, KC's home (portal spec 22, F2): four counters that link to their queues, then
// every client by stage in life order. Desktop: columns that scroll sideways inside the board (a kanban). Phone (390 px):
// stacked sections, each collapsible; Archived and Gone start collapsed. No drag and drop: stages move only through real
// actions, so the board can't drift from what happened.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { BoardStage, SiteStage } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { toast } from '../components/Toast';
import { apiErrorText } from '../lib/errors';
import { BoardCard } from './BoardCard';
import { useAdminCounts } from './adminNav';
import { Btn, Empty, STAGE_LABEL } from './adminUi';

const TILES = [
  { key: 'leads', label: 'Leads waiting', to: '/admin/leads', icon: 'inbox' },
  { key: 'lifecycle', label: 'Lifecycle needs you', to: '/admin/lifecycle', icon: 'rocket' },
  { key: 'outbox', label: 'Messages to approve', to: '/admin/outbox', icon: 'send' },
  { key: 'review', label: 'Reports to release', to: '/admin/review', icon: 'file' },
] as const;

export function AdminBoard() {
  const q = useQuery({ queryKey: ['admin-board'], queryFn: () => api.adminBoard(), refetchInterval: 60_000 });
  const counts = useAdminCounts().data;
  const [open, setOpen] = useState<Partial<Record<SiteStage, boolean>>>({});
  const isOpen = (s: BoardStage) => open[s.stage] ?? !s.collapsed;
  const total = (q.data ?? []).reduce((n, s) => n + s.cards.length, 0);

  const exportFleet = async () => {
    try {
      const f = await api.adminFleet();
      const url = URL.createObjectURL(new Blob([JSON.stringify(f, null, 2)], { type: 'application/json' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: 'fleet.json' });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast({ kind: 'error', message: apiErrorText(e, 'Couldn’t export the fleet.') }); }
  };

  return (
    <>
      <PageHeader title="Clients" subtitle={q.data ? `${total} sites, by stage. Each card says what to do next.` : 'Every client, by stage.'}
        action={<Btn icon="file" onClick={() => void exportFleet()} aria-label="Export fleet.json"><span className="hidden sm:inline">fleet.json</span></Btn>} />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {TILES.map((t) => {
          const n = counts?.[t.key];
          return (
            <Link key={t.key} to={t.to} className="min-h-11 rounded-lg border border-hair bg-surface p-4 hover:border-run">
              <div className="flex items-center gap-1.5 text-[13px] text-muted"><Icon name={t.icon} size={14} />{t.label}</div>
              <div className={`mt-1 font-head text-2xl font-medium ${n ? 'text-run' : 'text-ink'}`}>{n ?? '—'}</div>
            </Link>
          );
        })}
      </div>

      {q.isLoading && <Skeleton variant="card" count={3} />}
      {q.isError && <Empty>{apiErrorText(q.error, 'Couldn’t load the board.')}</Empty>}
      {q.data && (
        <div className="min-w-0 md:-mx-1 md:overflow-x-auto md:pb-3">
          <div className="flex flex-col gap-3 md:w-max md:flex-row md:items-start md:px-1">
            {q.data.map((s) => (
              <section key={s.stage} aria-label={STAGE_LABEL[s.stage]} className="min-w-0 rounded-lg border border-hair bg-surface/40 p-2 md:w-72 md:shrink-0">
                <button type="button" onClick={() => setOpen((o) => ({ ...o, [s.stage]: !isOpen(s) }))} aria-expanded={isOpen(s)}
                  className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-2 text-left">
                  <span className="font-head text-sm text-ink">{STAGE_LABEL[s.stage]} <span className="text-muted">{s.cards.length}</span></span>
                  <Icon name="chevron-right" size={16} className={`text-muted transition-transform ${isOpen(s) ? 'rotate-90' : ''}`} />
                </button>
                {isOpen(s) && (
                  <div className="mt-1 flex flex-col gap-2">
                    {s.cards.length ? s.cards.map((c) => <BoardCard key={c.slug} c={c} />)
                      : <p className="px-2 pb-2 text-sm text-muted">Nobody here.</p>}
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
